import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import getSafeImageSrc from 'editor/src/util/getSafeImageSrc';

import DesktopZoomControls from './DesktopZoomControls';
import {
  PREVIEW_DOUBLE_CLICK_SCALE,
  PREVIEW_RESET_VIEW,
  PREVIEW_SCALE_STEP,
  PreviewView,
  Size,
  clampPreviewView,
  zoomPreviewAt,
} from './previewZoomMath';

import styles from './index.module.scss';

interface ZoomableImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  src: string;
  alt: string;
  className?: string;
  containerClassName?: string;
  onLoad?: () => void;
  // Mouse zoom and pan: +/- buttons, wheel or trackpad pinch to zoom, drag to move,
  // double-click to toggle. Without it the image answers to touch: pinch, drag, double-tap.
  desktopZoom?: boolean;
  // Return to the fitted view whenever this changes (e.g. the carousel moved on).
  resetKey?: unknown;
}

const WHEEL_ZOOM_FACTOR = 0.995;
const DOUBLE_TAP_THRESHOLD = 300; // ms

interface Point {
  x: number;
  y: number;
}

const touchPoint = (touch: Touch): Point => ({ x: touch.clientX, y: touch.clientY });
const touchDistance = (a: Touch, b: Touch) => Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
const touchMidpoint = (a: Touch, b: Touch): Point => ({
  x: (a.clientX + b.clientX) / 2,
  y: (a.clientY + b.clientY) / 2,
});

function ZoomableImage({
  src,
  alt,
  className,
  containerClassName,
  onLoad,
  style,
  desktopZoom = false,
  resetKey,
}: ZoomableImageProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const [view, setView] = useState<PreviewView>(PREVIEW_RESET_VIEW);
  // A mouse drag or a touch gesture is in progress: the image follows the pointer with no easing.
  const [interacting, setInteracting] = useState(false);
  // The pointer at the last pan step: each move applies only its own step, so a zoom
  // part-way through a drag does not make the image jump.
  const lastPointer = useRef<Point | undefined>();
  const viewRef = useRef(view);
  viewRef.current = view;
  const lastTapTime = useRef(0);
  // The pinch as it started: the scale follows the finger distance relative to this.
  const pinch = useRef<{ distance: number; scale: number; midpoint: Point } | undefined>();

  const measure = useCallback((): { image: Size; container: Size } | undefined => {
    const container = containerRef.current;
    const image = imageRef.current;
    if (!container || !image) {
      return undefined;
    }
    // offsetWidth/Height ignore the transform, so this is the fitted size at scale 1.
    return {
      image: { width: image.offsetWidth, height: image.offsetHeight },
      container: { width: container.clientWidth, height: container.clientHeight },
    };
  }, []);

  const zoomAt = useCallback(
    (nextScale: (scale: number) => number, clientX?: number, clientY?: number) => {
      const sizes = measure();
      const rect = containerRef.current?.getBoundingClientRect();
      if (!sizes || !rect) {
        return;
      }
      const point =
        clientX === undefined || clientY === undefined
          ? { x: 0, y: 0 }
          : { x: clientX - rect.left - rect.width / 2, y: clientY - rect.top - rect.height / 2 };
      setView((current) => zoomPreviewAt(current, nextScale(current.scale), point, sizes.image, sizes.container));
    },
    [measure],
  );

  const panBy = useCallback(
    (stepX: number, stepY: number) => {
      const sizes = measure();
      if (!sizes) {
        return;
      }
      setView((current) =>
        clampPreviewView(
          { scale: current.scale, x: current.x + stepX, y: current.y + stepY },
          sizes.image,
          sizes.container,
        ),
      );
    },
    [measure],
  );

  const toggleZoomAt = useCallback(
    (clientX: number, clientY: number) => {
      if (viewRef.current.scale > 1) {
        setView(PREVIEW_RESET_VIEW);
      } else {
        zoomAt(() => PREVIEW_DOUBLE_CLICK_SCALE, clientX, clientY);
      }
    },
    [zoomAt],
  );

  useEffect(() => {
    setView(PREVIEW_RESET_VIEW);
  }, [resetKey, src]);

  // React registers touch listeners as passive, so preventDefault needs native listeners.
  useEffect(() => {
    const container = containerRef.current;
    if (!container || desktopZoom) {
      return undefined;
    }

    const onTouchStart = (e: TouchEvent) => {
      if (e.touches.length === 2) {
        // The pinch is ours, not the page's.
        e.preventDefault();
        pinch.current = {
          distance: touchDistance(e.touches[0], e.touches[1]),
          scale: viewRef.current.scale,
          midpoint: touchMidpoint(e.touches[0], e.touches[1]),
        };
        lastPointer.current = undefined;
        setInteracting(true);
        return;
      }
      if (e.touches.length !== 1) {
        return;
      }
      const touch = e.touches[0];
      const now = Date.now();
      const sinceLastTap = now - lastTapTime.current;
      lastTapTime.current = now;
      if (sinceLastTap > 0 && sinceLastTap < DOUBLE_TAP_THRESHOLD) {
        e.preventDefault();
        lastTapTime.current = 0;
        toggleZoomAt(touch.clientX, touch.clientY);
        return;
      }
      // A finger on the fitted image is a carousel swipe or a page scroll, not a pan.
      if (viewRef.current.scale > 1) {
        lastPointer.current = touchPoint(touch);
        setInteracting(true);
      }
    };

    const onTouchMove = (e: TouchEvent) => {
      if (e.touches.length === 2 && pinch.current) {
        e.preventDefault();
        const midpoint = touchMidpoint(e.touches[0], e.touches[1]);
        const scale = (pinch.current.scale * touchDistance(e.touches[0], e.touches[1])) / pinch.current.distance;
        // Fingers moving together drag the image; their spread zooms it about where they are.
        panBy(midpoint.x - pinch.current.midpoint.x, midpoint.y - pinch.current.midpoint.y);
        pinch.current.midpoint = midpoint;
        zoomAt(() => scale, midpoint.x, midpoint.y);
        return;
      }
      if (e.touches.length === 1 && lastPointer.current) {
        e.preventDefault();
        const point = touchPoint(e.touches[0]);
        panBy(point.x - lastPointer.current.x, point.y - lastPointer.current.y);
        lastPointer.current = point;
      }
    };

    const onTouchEnd = (e: TouchEvent) => {
      if (e.touches.length < 2) {
        pinch.current = undefined;
      }
      if (e.touches.length === 1) {
        // One finger lifted after a pinch: the other keeps dragging a zoomed image.
        lastPointer.current = viewRef.current.scale > 1 ? touchPoint(e.touches[0]) : undefined;
      }
      if (e.touches.length === 0) {
        lastPointer.current = undefined;
        setInteracting(false);
      }
    };

    container.addEventListener('touchstart', onTouchStart, { passive: false });
    container.addEventListener('touchmove', onTouchMove, { passive: false });
    container.addEventListener('touchend', onTouchEnd);
    container.addEventListener('touchcancel', onTouchEnd);
    return () => {
      container.removeEventListener('touchstart', onTouchStart);
      container.removeEventListener('touchmove', onTouchMove);
      container.removeEventListener('touchend', onTouchEnd);
      container.removeEventListener('touchcancel', onTouchEnd);
    };
  }, [desktopZoom, zoomAt, panBy, toggleZoomAt]);

  // React registers wheel listeners as passive, so preventDefault needs a native listener.
  useEffect(() => {
    const container = containerRef.current;
    if (!container || !desktopZoom) {
      return undefined;
    }
    // As on the canvas: a pinch (reported with ctrlKey) or Ctrl/Cmd + wheel zooms, a plain
    // scroll moves a zoomed image, and a plain scroll over the fitted image does nothing.
    const onWheel = (e: WheelEvent) => {
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        zoomAt((scale) => scale * WHEEL_ZOOM_FACTOR ** e.deltaY, e.clientX, e.clientY);
        return;
      }
      if (viewRef.current.scale <= 1) {
        return;
      }
      e.preventDefault();
      panBy(-e.deltaX, -e.deltaY);
    };
    container.addEventListener('wheel', onWheel, { passive: false });
    return () => container.removeEventListener('wheel', onWheel);
  }, [desktopZoom, zoomAt, panBy]);

  useEffect(() => {
    if (!desktopZoom || !interacting) {
      return undefined;
    }
    const onMove = (e: MouseEvent) => {
      // The button was released somewhere we never heard about (another window).
      if (e.buttons === 0) {
        setInteracting(false);
        return;
      }
      const last = lastPointer.current;
      if (!last) {
        return;
      }
      lastPointer.current = { x: e.clientX, y: e.clientY };
      panBy(e.clientX - last.x, e.clientY - last.y);
    };
    const onUp = () => setInteracting(false);
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    window.addEventListener('blur', onUp);
    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      window.removeEventListener('blur', onUp);
    };
  }, [desktopZoom, interacting, panBy]);

  const onMouseDown = (e: React.MouseEvent) => {
    if (!desktopZoom || view.scale <= 1 || e.button !== 0) {
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    lastPointer.current = { x: e.clientX, y: e.clientY };
    setInteracting(true);
  };

  const onDoubleClick = (e: React.MouseEvent) => {
    if (desktopZoom) {
      toggleZoomAt(e.clientX, e.clientY);
    }
  };

  const safeSrc = useMemo(() => getSafeImageSrc(src), [src]);

  const handleImageLoad = () => {
    onLoad?.();
  };

  const zoomed = view.scale > 1;
  return (
    <div
      ref={containerRef}
      className={`${styles.container} ${desktopZoom ? styles.desktopZoom : ''} ${containerClassName || ''}`}
      data-zoomed={zoomed}
      onMouseDown={onMouseDown}
      onDoubleClick={onDoubleClick}
    >
      <img
        ref={imageRef}
        src={safeSrc}
        alt={alt}
        draggable={false}
        className={`${styles.image} ${zoomed ? styles.grab : ''} ${interacting ? styles.grabbing : ''} ${
          className || ''
        }`}
        style={{
          ...style,
          transform: `translate(${view.x}px, ${view.y}px) scale(${view.scale})`,
          transition: interacting ? 'none' : undefined,
        }}
        onLoad={handleImageLoad}
      />
      {desktopZoom && (
        <DesktopZoomControls
          scale={view.scale}
          onZoomOut={() => zoomAt((scale) => scale - PREVIEW_SCALE_STEP)}
          onZoomIn={() => zoomAt((scale) => scale + PREVIEW_SCALE_STEP)}
          onReset={() => setView(PREVIEW_RESET_VIEW)}
        />
      )}
    </div>
  );
}

export default ZoomableImage;
